import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { OfflineDbService } from './offline-db.service';
import { OfflineCryptoService } from './offline-crypto.service';
import { buildContentKey, getCurrentSchoolSubdomain, getCurrentUserId } from './offline-cache-key';

// Small, generic cache for the app's OWN bootstrap calls (School/getschoolconfig,
// offline/session) — NOT the explicit-save machinery in offline-save.service.ts. Those two
// calls gate whether the router/banner render at all, so without a fallback, a reload while
// offline hits a hard "startupFailed" wall before the user's already-saved lesson content
// ever gets a chance to serve from its own cache. Deliberately simpler than the
// OFFLINE_ROUTE_ALLOWLIST machinery: no explicit-save requirement (D2 doesn't apply — this
// isn't lesson content, it's app infrastructure), no role gate (both responses are
// non-sensitive and identical for admin and student). Always caches on success for any
// logged-in user, regardless of the offline-mode feature flag — this is a baseline resiliency
// fix, not part of the opt-in save-a-lesson feature. Reads the token straight from
// localStorage rather than injecting UseraccessserviceService, which would be a circular
// dependency (that service already injects OfflineSessionService, a consumer of this one).
@Injectable({
  providedIn: 'root',
})
export class OfflineBootstrapCacheService {
  constructor(private offlineDb: OfflineDbService, private offlineCrypto: OfflineCryptoService) {}

  private currentIdentity(): { school: string; user: number } | null {
    const school = getCurrentSchoolSubdomain();
    const token = localStorage.getItem(environment.userTokenStore);
    const user = getCurrentUserId(token);
    if (!school || !user) return null;
    return { school, user };
  }

  async cache(kind: string, data: unknown): Promise<void> {
    const identity = this.currentIdentity();
    if (!identity) return; // not logged in (or subdomain unresolvable) — nothing to scope a key to
    const { school, user } = identity;

    const deviceKey = await this.offlineCrypto.getOrCreateDeviceKey();
    const plaintext = new TextEncoder().encode(JSON.stringify(data)).buffer;
    const { iv, ciphertext } = await this.offlineCrypto.encryptBlob(deviceKey, plaintext);
    const key = buildContentKey(school, user, kind, 'bootstrap', 'unversioned');

    await this.offlineDb.putContent({
      key,
      school,
      user,
      kind,
      resourceId: 'bootstrap',
      version: 'unversioned',
      iv,
      ciphertext,
      savedAt: Date.now(),
      bytes: plaintext.byteLength,
    });
  }

  async getCached<T>(kind: string): Promise<T | null> {
    const identity = this.currentIdentity();
    if (!identity) return null;
    const { school, user } = identity;

    const key = buildContentKey(school, user, kind, 'bootstrap', 'unversioned');
    const record = await this.offlineDb.getContent(key);
    if (!record) return null;

    try {
      const deviceKey = await this.offlineCrypto.getOrCreateDeviceKey();
      const plaintext = await this.offlineCrypto.decryptBlob(deviceKey, record.iv, record.ciphertext);
      return JSON.parse(new TextDecoder().decode(plaintext)) as T;
    } catch (err) {
      console.error(`[offline] failed to decrypt cached bootstrap data (${kind})`, err);
      return null;
    }
  }
}
